import { buildAttachmentUrl } from "./buildAttachmentUrl";
import { getPageIdFromHref } from "./getPageIdFromHref";

const PANEL_ICONS = {
    info: "fa-info-circle",
    note: "fa-sticky-note",
    tip: "fa-lightbulb-o",
    success: "fa-check-circle",
    warning: "fa-exclamation-triangle",
    error: "fa-times-circle"
};

const EMOTICONS = {
    tick: { symbol: "✓", className: "confluence-page__emoticon--tick", label: "Ja" },
    "check_mark": { symbol: "✓", className: "confluence-page__emoticon--tick", label: "Ja" },
    cross: { symbol: "✗", className: "confluence-page__emoticon--cross", label: "Nein" },
    "cross_mark": { symbol: "✗", className: "confluence-page__emoticon--cross", label: "Nein" },
    smile: { symbol: "🙂", className: "confluence-page__emoticon--smile", label: "Smile" },
    sad: { symbol: "🙁", className: "confluence-page__emoticon--sad", label: "Sad" },
    cheeky: { symbol: "😜", className: "confluence-page__emoticon--cheeky", label: "Cheeky" },
    laugh: { symbol: "😄", className: "confluence-page__emoticon--laugh", label: "Laugh" },
    wink: { symbol: "😉", className: "confluence-page__emoticon--wink", label: "Wink" },
    thumbs_up: { symbol: "👍", className: "confluence-page__emoticon--thumbs-up", label: "Thumbs up" },
    thumbs_down: { symbol: "👎", className: "confluence-page__emoticon--thumbs-down", label: "Thumbs down" },
    information: { symbol: "ℹ️", className: "confluence-page__emoticon--information", label: "Info" },
    question: { symbol: "❓", className: "confluence-page__emoticon--question", label: "Frage" },
    warning: { symbol: "⚠️", className: "confluence-page__emoticon--warning", label: "Warnung" },
    plus: { symbol: "➕", className: "confluence-page__emoticon--plus", label: "Plus" },
    minus: { symbol: "➖", className: "confluence-page__emoticon--minus", label: "Minus" },
    yellow_star: { symbol: "⭐", className: "confluence-page__emoticon--star", label: "Star" },
    red_star: { symbol: "⭐", className: "confluence-page__emoticon--star", label: "Star" },
    green_star: { symbol: "⭐", className: "confluence-page__emoticon--star", label: "Star" },
    blue_star: { symbol: "⭐", className: "confluence-page__emoticon--star", label: "Star" },
    light_on: { symbol: "💡", className: "confluence-page__emoticon--light-on", label: "Idee" },
    light_off: { symbol: "🔅", className: "confluence-page__emoticon--light-off", label: "Light off" },
    heart: { symbol: "❤️", className: "confluence-page__emoticon--heart", label: "Heart" },
    broken_heart: { symbol: "💔", className: "confluence-page__emoticon--broken-heart", label: "Broken heart" }
};

const GERMAN_CHAR_MAP = { "ä": "ae", "ö": "oe", "ü": "ue", "ß": "ss" };

const slugify = (text) => {
    return (text || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[äöüß]/g, (char) => GERMAN_CHAR_MAP[char] || char)
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[\s\u00A0]+/g, "-")
        .replace(/[^a-z0-9\-]+/g, "")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "") || "section";
};

const decodeEmojiId = (value) => {
    if (!value) {
        return "";
    }

    const parts = value
        .replace(/^atlassian-/, "")
        .split(/[-_]/)
        .filter((part) => /^[0-9a-fA-F]+$/.test(part));

    if (parts.length === 0) {
        return "";
    }

    try {
        const codepoints = parts
            .map((part) => parseInt(part, 16))
            .filter((n) => Number.isFinite(n) && n > 0);

        return codepoints.length > 0 ? String.fromCodePoint(...codepoints) : "";
    } catch (error) {
        return "";
    }
};

export const normalizeConfluenceHtml = (html, page) => {
    if (!html) {
        return "<p class=\"confluence-page__empty\">Es sind keine Inhalte verfügbar.</p>";
    }

    const pageId = page?.id;
    const parser = new DOMParser();
    const documentNode = parser.parseFromString(`<div>${html}</div>`, "text/html");
    const root = documentNode.body.firstElementChild || documentNode.body;

    const createElement = (tagName, className) => {
        const node = documentNode.createElement(tagName);

        if (className) {
            node.className = className;
        }
        return node;
    };

    const createTextWrapper = (tagName, textContent, className) => {
        const node = createElement(tagName, className);

        node.textContent = textContent;
        return node;
    };

    const createPanel = (typeKey, iconClass, innerHtml) => {
        const panel = createElement("div", `confluence-page__panel confluence-page__panel--${typeKey}`);
        const icon = createElement("span", `confluence-page__panel-icon fa ${iconClass}`);
        const body = createElement("div", "confluence-page__panel-body");

        if (innerHtml) {
            body.innerHTML = innerHtml;
        }
        panel.appendChild(icon);
        panel.appendChild(body);
        return panel;
    };

    assignHeadingIds(root);

    const buildTocList = (macroElement) => buildToc(macroElement, root, documentNode);

    Array.from(root.querySelectorAll("*")).forEach((element) => {
        const tagName = element.tagName.toLowerCase();

        switch (tagName) {
            case "ac:image":
                handleImage(element, documentNode, pageId, createTextWrapper);
                return;
            case "ac:link":
                handleLink(element, documentNode, pageId);
                return;
            case "ac:adf-extension":
                handleAdfExtension(element, documentNode, createPanel);
                return;
            case "ac:structured-macro":
                handleStructuredMacro(element, documentNode, createElement, createTextWrapper, createPanel, buildTocList);
                return;
            case "ac:emoticon":
                handleEmoticon(element, documentNode);
                return;
            default:
                break;
        }

        if (tagName.startsWith("ri:")) {
            element.remove();
            return;
        }

        if (tagName.startsWith("ac:")) {
            if (element.textContent?.trim()) {
                element.replaceWith(createTextWrapper("span", element.textContent.trim(), "confluence-page__macro-text"));
            } else {
                element.remove();
            }
        }
    });

    normalizeAnchors(root);

    return root.innerHTML;
};

function assignHeadingIds(root) {
    const usedHeadingIds = new Set();

    Array.from(root.querySelectorAll("h1, h2, h3, h4, h5, h6")).forEach((heading) => {
        const baseId = heading.getAttribute("id") || slugify(heading.textContent);
        let uniqueId = baseId;
        let counter = 2;

        while (usedHeadingIds.has(uniqueId)) {
            uniqueId = `${baseId}-${counter}`;
            counter += 1;
        }

        usedHeadingIds.add(uniqueId);
        heading.setAttribute("id", uniqueId);
    });
}

function buildToc(macroElement, root, documentNode) {
    const params = {};

    Array.from(macroElement.querySelectorAll("ac\\:parameter")).forEach((param) => {
        const name = param.getAttribute("ac:name");

        if (name) {
            params[name] = param.textContent?.trim();
        }
    });

    const minLevel = Math.max(1, parseInt(params.minLevel, 10) || 1);
    const maxLevel = Math.min(6, parseInt(params.maxLevel, 10) || 6);
    const headings = Array.from(root.querySelectorAll("h1, h2, h3, h4, h5, h6"))
        .map((heading) => ({
            level: parseInt(heading.tagName.substring(1), 10),
            id: heading.getAttribute("id"),
            text: heading.textContent?.trim() || ""
        }))
        .filter((item) => item.level >= minLevel && item.level <= maxLevel);

    if (headings.length === 0) {
        return null;
    }

    const container = documentNode.createElement("div");

    container.className = "confluence-page__toc";

    const baseLevel = headings[0].level;
    const rootList = documentNode.createElement("ul");
    const stack = [{ level: baseLevel, list: rootList }];

    headings.forEach((heading) => {
        while (stack.length > 1 && stack[stack.length - 1].level > heading.level) {
            stack.pop();
        }

        while (stack[stack.length - 1].level < heading.level) {
            const parentList = stack[stack.length - 1].list;
            let lastLi = parentList.lastElementChild;

            if (!lastLi) {
                lastLi = documentNode.createElement("li");
                parentList.appendChild(lastLi);
            }

            const nested = documentNode.createElement("ul");

            lastLi.appendChild(nested);
            stack.push({ level: stack[stack.length - 1].level + 1, list: nested });
        }

        const li = documentNode.createElement("li");
        const link = documentNode.createElement("a");
        const currentUrl = typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.search}`
            : "";

        link.setAttribute("href", `${currentUrl}#${heading.id}`);
        link.textContent = heading.text;
        li.appendChild(link);
        stack[stack.length - 1].list.appendChild(li);
    });

    container.appendChild(rootList);
    return container;
}

function handleImage(element, documentNode, pageId, createTextWrapper) {
    const attachment = element.querySelector("ri\\:attachment");
    const externalImage = element.querySelector("ri\\:url");
    const filename = attachment?.getAttribute("ri:filename");
    const imageSource = externalImage?.getAttribute("ri:value") || buildAttachmentUrl(pageId, filename);
    const imageWidth = element.getAttribute("ac:width");
    const imageAlt = filename || "Confluence image";
    const figure = documentNode.createElement("figure");

    figure.className = "confluence-page__image-wrapper";

    if (imageSource) {
        const image = documentNode.createElement("img");

        image.className = "confluence-page__image";
        image.setAttribute("alt", imageAlt);
        image.setAttribute("loading", "lazy");
        image.setAttribute("src", imageSource);
        image.setAttribute("data-filename", imageAlt);

        if (imageWidth) {
            image.setAttribute("style", `max-width:${imageWidth}px`);
        }

        figure.appendChild(image);
    } else {
        figure.appendChild(createTextWrapper("div", imageAlt, "confluence-page__image-placeholder"));
    }

    element.replaceWith(figure);
}

function handleLink(element, documentNode, pageId) {
    const linkedPage = element.querySelector("ri\\:page");
    const linkedAttachment = element.querySelector("ri\\:attachment");
    const linkBody = element.querySelector("ac\\:link-body, ac\\:plain-text-link-body");
    const linkedPageId = linkedPage?.getAttribute("ri:content-id");
    const linkedFilename = linkedAttachment?.getAttribute("ri:filename");
    const label = linkBody?.textContent?.trim()
        || linkedPage?.getAttribute("ri:content-title")
        || linkedFilename
        || element.textContent?.trim()
        || "Open link";

    const anchor = documentNode.createElement("a");

    anchor.textContent = label;

    if (linkedPageId) {
        anchor.setAttribute("href", "#");
        anchor.setAttribute("data-page-id", linkedPageId);
    } else if (linkedFilename) {
        const attachmentUrl = buildAttachmentUrl(pageId, linkedFilename);

        if (attachmentUrl) {
            anchor.setAttribute("href", attachmentUrl);
            anchor.setAttribute("target", "_blank");
            anchor.setAttribute("rel", "noreferrer");
        }
    }

    element.replaceWith(anchor);
}

function handleAdfExtension(element, documentNode, createPanel) {
    const panelNode = element.querySelector("ac\\:adf-node");
    const isPanel = panelNode?.getAttribute("type") === "panel";

    if (isPanel) {
        const panelTypeAttr = panelNode.querySelector("ac\\:adf-attribute[key='panel-type']");
        const panelContent = panelNode.querySelector("ac\\:adf-content");
        const panelType = (panelTypeAttr?.textContent?.trim() || "info").toLowerCase();
        const iconClass = PANEL_ICONS[panelType] || PANEL_ICONS.info;

        element.replaceWith(createPanel(panelType, iconClass, panelContent?.innerHTML));
        return;
    }

    const fallback = element.querySelector("ac\\:adf-fallback");

    if (fallback) {
        const wrapper = documentNode.createElement("div");

        wrapper.className = "confluence-page__macro";
        wrapper.innerHTML = fallback.innerHTML;
        element.replaceWith(wrapper);
    } else {
        element.remove();
    }
}

function handleStructuredMacro(element, documentNode, createElement, createTextWrapper, createPanel, buildTocList) {
    const macroName = (element.getAttribute("ac:name") || "").toLowerCase();
    const plainTextBody = element.querySelector("ac\\:plain-text-body");
    const richTextBody = element.querySelector("ac\\:rich-text-body");

    switch (macroName) {
        case "toc": {
            const tocNode = buildTocList(element);

            if (tocNode) {
                element.replaceWith(tocNode);
            } else {
                element.remove();
            }
            return;
        }
        case "code":
            if (plainTextBody?.textContent) {
                const pre = documentNode.createElement("pre");
                const code = documentNode.createElement("code");

                code.textContent = plainTextBody.textContent;
                pre.appendChild(code);
                element.replaceWith(pre);
                return;
            }
            break;
        default:
            break;
    }

    if (PANEL_ICONS[macroName] && richTextBody) {
        element.replaceWith(createPanel(macroName, PANEL_ICONS[macroName], richTextBody.innerHTML));
        return;
    }

    const macroClass = `confluence-page__macro${macroName ? ` confluence-page__macro--${macroName}` : ""}`;

    if (richTextBody) {
        const wrapper = createElement("div", macroClass);

        wrapper.innerHTML = richTextBody.innerHTML;
        element.replaceWith(wrapper);
        return;
    }

    if (element.textContent?.trim()) {
        element.replaceWith(createTextWrapper("div", element.textContent.trim(), macroClass));
    } else {
        element.remove();
    }
}

function handleEmoticon(element, documentNode) {
    const rawName = (element.getAttribute("ac:name") || "").toLowerCase();
    const rawFallbackAttr = element.getAttribute("ac:emoji-fallback") || "";
    const rawShortnameAttr = element.getAttribute("ac:emoji-shortname") || "";
    const rawEmojiId = element.getAttribute("ac:emoji-id") || "";
    const fallbackKey = rawFallbackAttr.replace(/:/g, "").toLowerCase();
    const shortnameKey = rawShortnameAttr.replace(/:/g, "").toLowerCase();
    const emoticon = EMOTICONS[rawName] || EMOTICONS[fallbackKey] || EMOTICONS[shortnameKey];

    const isUnicodeFallback = rawFallbackAttr
        && !/^:.*:$/.test(rawFallbackAttr.trim())
        && !/^[A-Za-z0-9_\s-]+$/.test(rawFallbackAttr.trim());

    let symbol = "";
    let label = rawName || shortnameKey || fallbackKey || "emoticon";
    let modifierClass = "";

    switch (true) {
        case Boolean(emoticon):
            symbol = emoticon.symbol;
            label = emoticon.label;
            modifierClass = emoticon.className;
            break;
        case Boolean(isUnicodeFallback):
            symbol = rawFallbackAttr;
            break;
        default: {
            const decoded = decodeEmojiId(rawEmojiId);

            symbol = decoded || rawFallbackAttr || rawShortnameAttr || rawName || "";
            break;
        }
    }

    const span = documentNode.createElement("span");

    span.className = `confluence-page__emoticon${modifierClass ? ` ${modifierClass}` : ""}`;
    span.setAttribute("role", "img");
    span.setAttribute("aria-label", label);
    span.textContent = symbol;
    element.replaceWith(span);
}

function normalizeAnchors(root) {
    Array.from(root.querySelectorAll("a[href]")).forEach((anchor) => {
        const href = anchor.getAttribute("href");

        if (!href) {
            return;
        }

        try {
            anchor.setAttribute("href", href.toString());
            const linkedPageId = getPageIdFromHref(href.toString());

            if (linkedPageId) {
                anchor.setAttribute("data-page-id", linkedPageId);
                anchor.removeAttribute("target");
                anchor.removeAttribute("rel");
            } else {
                anchor.setAttribute("target", "_blank");
                anchor.setAttribute("rel", "noreferrer");
            }
        } catch (error) {
        }
    });
}
